// View 6: Plan Options - Configure service date and options for each plan

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useOrderStore } from '@/stores/orderStore';
import { DatePicker } from '@/components/forms';
import { OnitWaterSection } from '@/components/plans';
import { api } from '@/api/client';
import { addPlanToCart, removePlanFromCart } from '@/services/cartService';
import { track } from '@/utils/analytics';
import { logger } from '@/utils/logger';
import {
  getAllowedDays,
  getEarliestServiceDate,
  getLeadTime,
  getScheduleDiagnostics,
  isSelectableServiceDate,
  isServiceDateValid,
} from '@/utils/scheduling';
import { logSinglePlanSchedule } from '@/utils/scheduleLogging';
import type { CartPlanOption } from '@/types';

interface PlanOption {
  id: string;
  name: string;
  type: 'check' | 'radio' | 'single' | 'addon';
  price?: number;
  uPrice?: number;        // Upfront price from API
  mPrice?: number;
  helpIcon?: string;      // Tooltip text from API
  required?: boolean;     // API field name
  isRequired?: boolean;   // Legacy/backwards compatibility
  isSelected?: boolean; // Pre-selected by default
  values?: string; // 'required' indicates required
  choices?: Array<{ value: string; label: string; price?: number; mPrice?: number }>;
}

interface PlanDetails {
  plan: {
    id: string;
    name: string;
    vendorName: string;
    price?: number;
    mPrice?: number;
    shortDescription?: string;
    longDescription?: string;
  };
  options: PlanOption[];
  holidays?: Array<{ name: string; date: string }>;
}

export function PlanOptionsView() {
  const { planId } = useParams<{ planId: string }>();
  return <PlanOptionsContent key={planId || 'missing-plan'} planId={planId} />;
}

function PlanOptionsContent({ planId }: { planId?: string }) {
  const navigate = useNavigate();
  const {
    selectedServices,
    markPlanConfigured,
    getNextPlanId,
    userId,
    personalInfo,
    onitSelected,
    onitPlan,
    setOnitSelected,
  } = useOrderStore();

  const [selectedServiceDate, setSelectedServiceDate] = useState('');
  const [userSelectedOptions, setUserSelectedOptions] = useState<Record<string, string | boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [errorOptions, setErrorOptions] = useState<Set<string>>(new Set()); // Track which options have errors
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double-click race condition

  // Find the service info for this plan (needed early for scheduling constraints)
  const selectedService = selectedServices.find((s) => s.planId === planId);
  const serviceName = selectedService?.type
    ? selectedService.type.charAt(0).toUpperCase() + selectedService.type.slice(1)
    : 'Service';

  const isInternet = selectedService?.type === 'internet';
  const isWater = selectedService?.type === 'water';

  // Build allowed-days array from vendor scheduling flags [Sun=0, Mon=1, ..., Sat=6].
  const allowedDays = useMemo(() => getAllowedDays(selectedService), [selectedService]);
  const scheduleDiagnostics = useMemo(() => getScheduleDiagnostics(selectedService), [selectedService]);

  const leadTime = getLeadTime(selectedService);

  // Fetch plan details (must be before holiday/date logic that depends on it)
  const { data: planDetails, isLoading, isError, error: planDetailsError } = useQuery({
    queryKey: ['planDetails', planId],
    queryFn: async () => {
      const response = await api.get<PlanDetails>(
        `/api/2turniton/services/${serviceName}/plans/${planId}`,
        { userId: userId || undefined }
      );
      // api.get never throws — it resolves to { success, data, error }. Surface the real
      // failure reason (e.g. "Not found", "HTTP 500: ...", "Network error...") so React
      // Query's `error` carries it. Returning undefined here would trip React Query's
      // generic "data cannot be undefined" error, leaving plan_details_load with no detail.
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to load plan details');
      }
      return response.data;
    },
    enabled: !!planId && !!serviceName,
  });

  // Build a Set of holiday date strings (YYYY-MM-DD) for O(1) lookup.
  // API sends dates as UTC ISO strings — extract just the date portion.
  const holidaySet = useMemo(() => {
    const set = new Set<string>();
    planDetails?.holidays?.forEach((h) => {
      set.add(h.date.split('T')[0]);
    });
    return set;
  }, [planDetails?.holidays]);

  // Compute earliest allowed date using the vendor's business-day lead time.
  const minDate = useMemo(() => {
    return getEarliestServiceDate(leadTime, allowedDays, holidaySet);
  }, [leadTime, allowedDays, holidaySet]);

  const maxDateObj = new Date();
  maxDateObj.setDate(maxDateObj.getDate() + 90);
  const maxDate = maxDateObj.toISOString().split('T')[0];

  // Filter function: only allow days the vendor operates and exclude holidays
  const filterDate = useCallback((date: Date) => {
    return isSelectableServiceDate(date, allowedDays, holidaySet);
  }, [allowedDays, holidaySet]);

  const preferredMoveDate = personalInfo?.moveDate;
  const defaultServiceDate = useMemo(() => {
    if (!preferredMoveDate || !planDetails) return '';
    // Only auto-populate if the preferred move date is valid for this vendor.
    if (isServiceDateValid(preferredMoveDate, minDate, allowedDays, holidaySet)) {
      return preferredMoveDate;
    }
    return '';
  }, [preferredMoveDate, minDate, allowedDays, holidaySet, planDetails]);

  const serviceDate = selectedServiceDate || defaultServiceDate;
  const serviceDateSource = selectedServiceDate ? 'user-selected' : defaultServiceDate ? 'preferred-move-date' : 'empty';

  const handleServiceDateChange = useCallback((value: string) => {
    setSelectedServiceDate(value);
    setError(null);
  }, []);

  useEffect(() => {
    if (!selectedService || !planDetails) return;

    logSinglePlanSchedule({
      area: 'plan-options',
      serviceName,
      stage: 'plan-options-loaded',
      zipCode: useOrderStore.getState().address?.zip,
      userId: userId || undefined,
      plan: selectedService,
      extra: {
        planId,
        detailVendorName: planDetails.plan?.vendorName,
        minDate,
        maxDate,
        allowedDays,
        holidayCount: holidaySet.size,
        preferredMoveDate,
        preferredMoveDateValid: preferredMoveDate
          ? isServiceDateValid(preferredMoveDate, minDate, allowedDays, holidaySet)
          : null,
        defaultServiceDate,
        serviceDate,
        serviceDateSource,
      },
    });
  }, [
    selectedService,
    planDetails,
    serviceName,
    userId,
    planId,
    minDate,
    maxDate,
    allowedDays,
    holidaySet,
    preferredMoveDate,
    defaultServiceDate,
    serviceDate,
    serviceDateSource,
  ]);

  // Helper to check if an option is required (API may send it in different ways)
  const isOptionRequired = (option: PlanOption): boolean => {
    return option.required === true || option.isRequired === true || option.values === 'required';
  };

  // Helper to check if an option should be disabled (required + pre-selected)
  const isOptionDisabled = (option: PlanOption): boolean => {
    return isOptionRequired(option) && option.isSelected === true;
  };

  // Compute pre-selected options from API data and merge with user selections
  const selectedOptions = useMemo(() => {
    const preSelected: Record<string, string | boolean> = {};
    planDetails?.options?.forEach((option) => {
      if (option.isSelected) {
        preSelected[option.id] = true;
      }
    });
    // User selections override pre-selected values
    return { ...preSelected, ...userSelectedOptions };
  }, [planDetails?.options, userSelectedOptions]);

  // Compute whether form is valid (service date + all required options filled)
  const isFormValid = useMemo(() => {
    if (!isServiceDateValid(serviceDate, minDate, allowedDays, holidaySet)) return false;

    const options = planDetails?.options || [];
    for (const option of options) {
      if (isOptionRequired(option) && !selectedOptions[option.id]) {
        return false;
      }
    }
    return true;
  }, [serviceDate, minDate, allowedDays, holidaySet, planDetails?.options, selectedOptions]);

  useEffect(() => {
    if (isError) {
      track('checkout_error', {
        error_context: 'plan_details_load',
        error_message:
          planDetailsError instanceof Error
            ? planDetailsError.message
            : String(planDetailsError),
        plan_id: planId,
        service_type: selectedService?.type,
      });
    }
  }, [isError, planDetailsError, planId, selectedService?.type]);

  const handleOptionChange = (optionId: string, value: string | boolean, option: PlanOption) => {
    // Don't allow unchecking disabled options
    if (isOptionDisabled(option) && !value) {
      return;
    }

    setUserSelectedOptions((prev) => ({ ...prev, [optionId]: value }));

    // Clear error for this option if it was in error state
    if (errorOptions.has(optionId)) {
      setErrorOptions((prev) => {
        const next = new Set(prev);
        next.delete(optionId);
        return next;
      });
    }
  };

  // Handle ONIT checkbox toggle - sync with backend cart immediately
  const handleOnitChange = (checked: boolean) => {
    setOnitSelected(checked);

    track(checked ? 'onit_selected' : 'onit_deselected', {
      zip: useOrderStore.getState().address?.zip,
      flow_type: useOrderStore.getState().flowType,
    });

    // Sync with backend cart immediately on toggle
    if (userId && onitPlan) {
      const onitPlanId = onitPlan.id || onitPlan.ID || '';
      if (checked) {
        addPlanToCart(userId, {
          planId: onitPlanId,
          vendorId: onitPlan.vendorId,
          serviceName: 'Water Filtration',
        });
      } else {
        removePlanFromCart(userId, onitPlanId);
      }
    }
  };

  const handleContinue = async () => {
    // Guard against double-click race condition
    if (isSubmitting) return;

    setError(null);
    setErrorOptions(new Set());

    let hasError = false;
    const newErrorOptions = new Set<string>();

    // Check service date
    if (!serviceDate) {
      setError('Please select a service start date');
      logger.warn(
        { area: 'plan-options', userId: userId || undefined },
        'Blocked continue with missing service start date',
        {
          serviceName,
          planId,
          selectedService,
          minDate,
          allowedDays,
          holidayCount: holidaySet.size,
          preferredMoveDate,
          defaultServiceDate,
          scheduleDiagnostics,
        }
      );
      hasError = true;
    } else if (!isServiceDateValid(serviceDate, minDate, allowedDays, holidaySet)) {
      setError('Please select a service start date available for this provider');
      logger.warn(
        { area: 'plan-options', userId: userId || undefined },
        'Blocked unavailable service start date',
        {
          serviceName,
          planId,
          serviceDate,
          serviceDateSource,
          selectedService,
          minDate,
          allowedDays,
          holidays: Array.from(holidaySet).slice(0, 25),
          preferredMoveDate,
          defaultServiceDate,
          scheduleDiagnostics,
        }
      );
      hasError = true;
    }

    // Check required options
    const options = planDetails?.options || [];
    for (const option of options) {
      if (isOptionRequired(option) && !selectedOptions[option.id]) {
        newErrorOptions.add(option.id);
        hasError = true;
      }
    }

    if (newErrorOptions.size > 0) {
      setErrorOptions(newErrorOptions);
      if (!error) {
        setError('Please select all required options');
      }
    }

    if (hasError) {
      return;
    }

    // Set submitting to prevent double-clicks
    setIsSubmitting(true);

    // Collect cart sync promises so we can await them before navigating
    const cartPromises: Promise<unknown>[] = [];
    const requireCartSuccess = async <T extends { success: boolean; error?: string }>(
      operation: Promise<T>,
      label: string
    ) => {
      const result = await operation;
      if (!result.success) {
        throw new Error(`${label}: ${result.error || 'unknown cart sync error'}`);
      }
      return result;
    };

    // For Water plans, sync ONIT selection to cart before continuing
    if (isWater && userId && onitPlan) {
      const onitPlanId = onitPlan.id || onitPlan.ID || '';
      if (onitSelected) {
        cartPromises.push(requireCartSuccess(
          addPlanToCart(userId, {
            planId: onitPlanId,
            vendorId: onitPlan.vendorId,
            serviceName: 'Water Filtration',
          }),
          'Add water filtration plan'
        ));
      } else {
        cartPromises.push(requireCartSuccess(
          removePlanFromCart(userId, onitPlanId),
          'Remove water filtration plan'
        ));
      }
    }

    // Build planOptions for the cart API
    const planOptions: CartPlanOption[] = [];
    for (const option of options) {
      const required = isOptionRequired(option);
      const value = selectedOptions[option.id];

      // Include required options always; optional only if user selected them
      if (!required && !value) continue;

      if (option.type === 'check') {
        planOptions.push({ id: option.id, type: 'check', isSelected: true });
      } else if (option.type === 'radio') {
        planOptions.push({ id: option.id, type: 'radio', selectedValue: value as string });
      } else if (option.type === 'single') {
        planOptions.push({ id: option.id, type: 'single', selectedInt: value as unknown as number });
      }
    }

    // Send plan + options to cart API
    if (userId && selectedService) {
      cartPromises.push(requireCartSuccess(
        addPlanToCart(userId, {
          planId: planId!,
          vendorId: selectedService.vendorId,
          serviceName,
          planOptions: planOptions.length > 0 ? planOptions : undefined,
        }),
        `Add ${serviceName} plan`
      ));
    }

    // Wait for all cart API calls to complete before navigating
    const results = await Promise.allSettled(cartPromises);
    let cartSyncFailed = false;
    for (const result of results) {
      if (result.status === 'rejected') {
        cartSyncFailed = true;
        logger.warn({ area: 'plan-options' }, `Cart sync failed: ${result.reason}`);
      }
    }

    if (cartSyncFailed) {
      setError('We could not save your selected plan. Please try again before continuing.');
      setIsSubmitting(false);
      return;
    }

    // Save configured plan
    logger.info(
      { area: 'plan-options', userId: userId || undefined },
      'Saving configured plan service date',
      {
        serviceName,
        planId,
        serviceDate,
        serviceDateSource,
        selectedService,
        minDate,
        allowedDays,
        holidayCount: holidaySet.size,
        scheduleDiagnostics,
      }
    );

    markPlanConfigured({
      planId: planId!,
      serviceDate,
      holidays: Array.from(holidaySet),
      options: Object.entries(selectedOptions).map(([id, value]) => ({
        id,
        type: typeof value === 'boolean' ? 'check' : 'radio',
        value,
      })),
    });

    track('checkout_plan_option_completed', {
      plan_id: planId,
      service_type: selectedService?.type,
      vendor_name: plan?.vendorName || selectedService?.vendorName,
    });

    // Navigate to next plan or checkout
    const nextPlanId = getNextPlanId(planId!);
    if (nextPlanId) {
      navigate(`/plan-options/${nextPlanId}`);
    } else {
      const configuredTypes = selectedServices.map(s => s.type);
      track('checkout_plans_configured', {
        zip: useOrderStore.getState().address?.zip,
        flow_type: useOrderStore.getState().flowType,
        services_configured: configuredTypes,
        total_services: configuredTypes.length,
        has_onit: onitSelected,
      });
      navigate('/checkout');
    }
  };

  if (isLoading) {
    return (
      <section className="view" style={{ display: 'block' }}>
        <div className="container">
          <h1 className="text--center mb-6">Loading plan details...</h1>
        </div>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="view" style={{ display: 'block' }}>
        <div className="container">
          <h1 className="text--center mb-6">Customize Your {serviceName} Plan</h1>
          <div className="error-notice mb-4">
            <p className="error-notice__text">
              We encountered an error loading plan details. Please try again.
            </p>
          </div>
          <div className="action-row">
            <button type="button" className="btn btn--back" onClick={() => navigate(-1)}>
              GO BACK
            </button>
          </div>
        </div>
      </section>
    );
  }

  const plan = planDetails?.plan;
  const options = planDetails?.options || [];

  // Calculate totals
  const calculateUpfrontTotal = () => {
    let total = plan?.price || 0;
    options.forEach((option) => {
      if (option.type === 'check' && selectedOptions[option.id]) {
        if (option.price) total += option.price;
        if (option.uPrice) total += option.uPrice;
      } else if (option.type === 'radio' && selectedOptions[option.id]) {
        const choice = option.choices?.find((c) => c.value === selectedOptions[option.id]);
        if (choice?.price) total += choice.price;
      }
    });
    return total;
  };

  const calculateMonthlyTotal = () => {
    let total = plan?.mPrice || 0;
    options.forEach((option) => {
      if (option.type === 'check' && selectedOptions[option.id] && option.mPrice) {
        total += option.mPrice;
      } else if (option.type === 'radio' && selectedOptions[option.id]) {
        const choice = option.choices?.find((c) => c.value === selectedOptions[option.id]);
        if (choice?.mPrice) total += choice.mPrice;
      }
    });
    return total;
  };

  const formatPrice = (price: number) => {
    return price === 0 ? 'Free' : `$${price.toFixed(2)}`;
  };

  return (
    <section className="view" style={{ display: 'block' }}>
      <div className="container">
        <h1 className="text--center mb-6">Customize Your {serviceName} Plan</h1>

        {/* Home Water Filtration Section - shown at top for Water plans */}
        {isWater && onitPlan && (
          <div className="onit-filtration-section">
            <h2 className="onit-filtration-section__header">Home Water Filtration</h2>
            <OnitWaterSection
              checked={onitSelected}
              onChange={handleOnitChange}
            />
          </div>
        )}

        {/* Plan Header */}
        <div className="plan-options-header mb-6">
          <div className="plan-options-header__vendor">
            {plan?.vendorName || selectedService?.vendorName || 'Provider'}
          </div>
          {!isInternet && (
            <div className="plan-options-header__plan">
              {plan?.name || selectedService?.name || 'Plan'}
            </div>
          )}
        </div>

        {/* Service Date Section */}
        <div className="service-date-section">
          <div className="service-date-section__header">
            <h3 className="service-date-section__title">
              When would you like service to start? <span className="required">*</span>
            </h3>
            <p className="service-date-section__subtitle">
              Select your preferred start date below
            </p>
          </div>
          <div className="service-date-section__picker-wrapper">
            <DatePicker
              id="serviceDate"
              value={serviceDate}
              onChange={handleServiceDateChange}
              minDate={minDate}
              maxDate={maxDate}
              filterDate={filterDate}
              required
            />
          </div>
          <p className="service-date-section__info">
            Your service will be activated on the selected date
          </p>
        </div>

        {/* Plan Options */}
        {options.length > 0 && (
          <div className="plan-options-list">
            {options
              .filter((option) => !isInternet || isOptionRequired(option))
              .sort((a, b) => {
                const aRequired = isOptionRequired(a);
                const bRequired = isOptionRequired(b);
                if (aRequired !== bRequired) return aRequired ? -1 : 1;
                return a.name.localeCompare(b.name);
              })
              .map((option) => {
              const isSelected = !!selectedOptions[option.id];
              const required = isOptionRequired(option);
              const disabled = isOptionDisabled(option);
              const hasError = errorOptions.has(option.id);

              const cardClasses = [
                'option-card',
                isSelected ? 'option-card--selected' : '',
                option.type === 'radio' ? 'option-card--radio' : '',
                disabled ? 'option-card--disabled' : '',
                hasError ? 'option-card--error' : '',
              ].filter(Boolean).join(' ');

              return (
                <div
                  key={option.id}
                  className={cardClasses}
                  data-option-id={option.id}
                  data-option-type={option.type}
                  data-required={required}
                  data-service-name={serviceName}
                  data-plan-id={planId}
                >
                  <div
                    className={`option-card__header${disabled ? ' option-card__content--disabled' : ''}`}
                    onClick={() => {
                      if (option.type === 'check' && !disabled) {
                        handleOptionChange(option.id, !selectedOptions[option.id], option);
                      }
                    }}
                  >
                    {option.type === 'check' && (
                      <div className="option-card__checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="option-card__checkbox"
                          checked={!!selectedOptions[option.id]}
                          onChange={(e) => handleOptionChange(option.id, e.target.checked, option)}
                          disabled={disabled}
                          data-option-id={option.id}
                          data-required={required}
                        />
                      </div>
                    )}
                    <div className={`option-card__content${option.type === 'radio' ? ' option-card__content--full' : ''}${disabled ? ' option-card__content--disabled' : ''}`}>
                      <div className="option-card__title-row">
                        <span className="option-card__name">{option.name}</span>
                        {required ? (
                          <span className="option-card__badge option-card__badge--required">
                            Required
                          </span>
                        ) : option.type === 'addon' ? (
                          <span className="option-card__badge option-card__badge--addon">
                            Add-on
                          </span>
                        ) : option.type === 'check' ? (
                          <span className="option-card__badge option-card__badge--optional">
                            Optional
                          </span>
                        ) : null}
                      </div>
                      {option.helpIcon && (
                        <p className="option-card__help-text">{option.helpIcon}</p>
                      )}
                      {option.type === 'check' && (
                        <div className="option-card__pricing">
                          {option.price !== undefined && (
                            <span className={`option-card__price${option.price === 0 ? ' option-card__price--free' : ''}`}>
                              {formatPrice(option.price)}
                              <span className="option-card__price-label">one-time</span>
                            </span>
                          )}
                          {option.uPrice !== undefined && option.uPrice > 0 && (
                            <span className="option-card__price">
                              {formatPrice(option.uPrice)}
                              <span className="option-card__price-label">one-time</span>
                            </span>
                          )}
                          {option.mPrice !== undefined && option.mPrice > 0 && (
                            <span className="option-card__price">
                              {formatPrice(option.mPrice)}
                              <span className="option-card__price-label">/month</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Radio choices */}
                  {option.type === 'radio' && option.choices && (
                    <div className="option-card__radio-group">
                      {option.choices.map((choice) => (
                        <label key={choice.value} className="option-card__radio-choice">
                          <input
                            type="radio"
                            className="option-card__radio"
                            name={option.id}
                            value={choice.value}
                            checked={selectedOptions[option.id] === choice.value}
                            onChange={() => handleOptionChange(option.id, choice.value, option)}
                            data-option-id={option.id}
                            data-choice-value={choice.value}
                          />
                          <span className="option-card__radio-label">{choice.label}</span>
                          {(choice.price !== undefined || choice.mPrice !== undefined) && (
                            <span className={`option-card__price${choice.price === 0 ? ' option-card__price--free' : ''}`}>
                              {choice.price !== undefined && formatPrice(choice.price)}
                              {choice.mPrice !== undefined && choice.mPrice > 0 && (
                                <span className="option-card__price-label">
                                  {' '}+ {formatPrice(choice.mPrice)}/mo
                                </span>
                              )}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Long Description */}
        {plan?.longDescription && (() => {
          const containsHtml = /<[a-z][\s\S]*>/i.test(plan.longDescription);
          const displayContent = descriptionExpanded
            ? plan.longDescription
            : plan.longDescription.slice(0, 200) + (plan.longDescription.length > 200 ? '...' : '');

          return (
            <div className="plan-long-description">
              {containsHtml ? (
                <div
                  className={`plan-long-description__content plan-long-description__content--html${descriptionExpanded ? ' plan-long-description__content--expanded' : ''}`}
                  dangerouslySetInnerHTML={{ __html: displayContent }}
                />
              ) : (
                <div className={`plan-long-description__content${descriptionExpanded ? ' plan-long-description__content--expanded' : ''}`}>
                  {displayContent}
                </div>
              )}
              {plan.longDescription.length > 200 && (
                <button
                  type="button"
                  className="plan-long-description__toggle"
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                >
                  {descriptionExpanded ? 'Show Less' : 'Read More'}
                </button>
              )}
            </div>
          );
        })()}

        {/* Short Description as fallback */}
        {!plan?.longDescription && plan?.shortDescription && (
          <div className="plan-long-description">
            <div className="plan-long-description__content">
              {plan.shortDescription}
            </div>
          </div>
        )}

        {/* Price Summary - only for Water plans */}
        {isWater && <div className="plan-price-summary">
          <h3 className="plan-price-summary__title">Summary</h3>

          {/* Upfront Costs */}
          <div className="plan-price-summary__section">
            <h4 className="plan-price-summary__section-title">Up Front Costs</h4>
            <div className="plan-price-summary__items">
              {plan?.price !== undefined && plan.price > 0 && (
                <div className="plan-price-summary__item">
                  <span>Setup Fee</span>
                  <span>{formatPrice(plan.price)}</span>
                </div>
              )}
              {options.map((option) => {
                if (option.type === 'check' && selectedOptions[option.id]) {
                  const upfront = (option.price || 0) + (option.uPrice || 0);
                  if (upfront > 0) {
                    return (
                      <div key={option.id} className="plan-price-summary__item">
                        <span>{option.name}</span>
                        <span>{formatPrice(upfront)}</span>
                      </div>
                    );
                  }
                }
                if (option.type === 'radio' && selectedOptions[option.id]) {
                  const choice = option.choices?.find((c) => c.value === selectedOptions[option.id]);
                  if (choice?.price) {
                    return (
                      <div key={option.id} className="plan-price-summary__item">
                        <span>{option.name}: {choice.label}</span>
                        <span>{formatPrice(choice.price)}</span>
                      </div>
                    );
                  }
                }
                return null;
              })}
              {calculateUpfrontTotal() === 0 && (
                <div className="plan-price-summary__item plan-price-summary__item--note">
                  No upfront costs
                </div>
              )}
            </div>
            <div className="plan-price-summary__total">
              <span>Total Due Today</span>
              <span>{formatPrice(calculateUpfrontTotal())}</span>
            </div>
          </div>

          {/* Monthly Costs */}
          <div className="plan-price-summary__section">
            <h4 className="plan-price-summary__section-title">Monthly Costs</h4>
            <p className="plan-price-summary__disclaimer">
              Actual bills vary based on actual water usage.
            </p>
            <div className="plan-price-summary__items">
              {plan?.mPrice !== undefined && plan.mPrice > 0 && (
                <div className="plan-price-summary__item">
                  <span>Base Plan</span>
                  <span>{formatPrice(plan.mPrice)}</span>
                </div>
              )}
              {options.map((option) => {
                if (option.type === 'check' && selectedOptions[option.id] && option.mPrice) {
                  return (
                    <div key={option.id} className="plan-price-summary__item">
                      <span>{option.name}</span>
                      <span>{formatPrice(option.mPrice)}</span>
                    </div>
                  );
                }
                if (option.type === 'radio' && selectedOptions[option.id]) {
                  const choice = option.choices?.find((c) => c.value === selectedOptions[option.id]);
                  if (choice?.mPrice) {
                    return (
                      <div key={option.id} className="plan-price-summary__item">
                        <span>{option.name}: {choice.label}</span>
                        <span>{formatPrice(choice.mPrice)}</span>
                      </div>
                    );
                  }
                }
                return null;
              })}
            </div>
            <div className="plan-price-summary__total">
              <span>Monthly</span>
              <span>
                {calculateMonthlyTotal() > 0
                  ? `${formatPrice(calculateMonthlyTotal())} + water usage`
                  : 'Based on water usage'}
              </span>
            </div>
          </div>
        </div>}

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Action Buttons */}
        <div className="action-row">
          <button type="button" className="btn btn--back" onClick={() => navigate(-1)}>
            GO BACK
          </button>
          <button type="button" className="btn" onClick={handleContinue} disabled={!isFormValid || isSubmitting}>
            {isSubmitting ? 'PROCESSING...' : 'CONTINUE'}
          </button>
        </div>
      </div>
    </section>
  );
}
